/**
 * `/flows` — the Flow pillar's one destination, slice 69.
 *
 * The list of flows, and the way into the builder: "New flow" goes to
 * `/flows/new` (pick a starting template), a row opens `/flows/:id`. A
 * workspace without the `flow` feature gets the API's 403 sentence
 * (`FLOW_NOT_ENTITLED_MESSAGE`) through the ordinary error state — the sidebar
 * does not link here in that case, but a bookmark still might.
 */
import { PageHeader, buttonVariants } from '@wend/ui';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { listFlows } from '../../lib/api/flows';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { useAsyncData } from '../../lib/use-async-data';
import { FlowsContent } from './FlowsContent';

export function FlowsPage() {
  const { status, data, error, reload, loadingFirst } = useAsyncData((signal) => listFlows(signal), []);

  return (
    <AppLayout
      title="Flows"
      actions={
        <Link to="/flows/new" className={buttonVariants({ size: 'sm' })}>
          <Plus aria-hidden="true" className="size-4" />
          New flow
        </Link>
      }
    >
      <PageHeader
        eyebrow={PILLAR_IDENTITY.flow.label}
        icon={PILLAR_IDENTITY.flow.icon}
        lede="Journeys people walk on their own — triggered by a segment, a schedule, a reply or a lead, with waits, checks and branches. Nothing launches until you do."
      />
      <div className="flex flex-col gap-6" data-role="flows-page">
        {status === 'error' && data === null && (
          <ErrorState
            error={error}
            title="Could not load your flows"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}
        {loadingFirst && <p className="text-[0.9rem] text-muted">Loading flows…</p>}
        {data !== null && <FlowsContent flows={data.flows} />}
      </div>
    </AppLayout>
  );
}
